import { Card, Grid } from "@mui/material";
import type { DateRange } from "@mui/x-date-pickers-pro";
import { Methods } from "api";
import { TYPE_ERROR } from "common/constants/common";
import { INITIAL_FEATURE_FLAG_VALUES } from "common/constants/initialValues";
import * as MESSAGES from "common/constants/messages";
import { CLAIM_STATUS } from "common/constants/options";
import type { CSVData, CSVHeader, ColumnData, FeatureFlags, FilterData, TypeClaim } from "common/types/commonTypes";
// Hooks
import ProDataGrid from "components/proDataGrid/ProDataGrid";
import { ProDataGridProvider } from "components/proDataGrid/ProDataGridProvider";
import dayjs, { type Dayjs } from "dayjs";
import { useAPI, useAuth, usePopups } from "hooks";
import { isEmpty } from "lodash";
import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Claim, ModelClaimFilterInputNew, OrgUserResponse } from "s12-library/types";
import { mapCSVData, mapTableData, tableColumnList } from "utils/commonFunctions";
import PaidDetails from "./PaidDetails";

const PaidWrapper = () => {
  return (
    <ProDataGridProvider>
      <Paid />
    </ProDataGridProvider>
  );
};

const Paid: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [rowData, setRowData] = useState<TypeClaim[]>([]);
  const [clickedRowId, setClickedRowId] = useState<string>("");
  const [searchData, setSearchData] = useState<FilterData | null>(null);
  const [yearFilterData, setYearFilterData] = useState<ColumnData | null>(null);
  const [ccgFilterData, setCcgFilterData] = useState<ColumnData | null>(null);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([dayjs().subtract(1, "day"), dayjs()]);
  const [csvData, setCsvData] = useState<[CSVHeader[], CSVData[]]>([[], []]);
  const [selectedCsvData, setSelectedCsvData] = useState<[CSVHeader[], CSVData[]]>([[], []]);
  const { handleBannerMessage } = usePopups();

  // Queries
  const {
    data: paidClaimData,
    loading: paidClaimLoading,
    error: paidClaimError,
    trigger: getPaidClaim,
  } = useAPI<
    Claim[],
    {
      filter: ModelClaimFilterInputNew;
    }
  >({
    method: Methods.GET,
    fieldName: "listClaims",
    manual: true,
  });

  const {
    data: dateRangeData,
    loading: dateRangeDataLoading,
    error: dateRangeDataError,
    trigger: getDateRangeData,
  } = useAPI<
    Claim[],
    {
      filter: ModelClaimFilterInputNew;
    }
  >({
    method: Methods.GET,
    fieldName: "listClaims",
    manual: true,
    args: {
      filter: {
        status: CLAIM_STATUS.paid,
        receivedDateFrom: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
        receivedDateTo: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
      },
    },
  });

  const {
    data: ccgData,
    loading: ccgLoading,
    error: ccgError,
    trigger: getCcg,
  } = useAPI<
    OrgUserResponse[],
    {
      id: string;
    }
  >({
    method: Methods.GET,
    fieldName: "getClaimUserOrgs",
    manual: true,
  });

  const ccgList = useMemo(
    () =>
      ccgData?.map((item) => {
        return {
          key: item.id,
          value: item.name,
        };
      }) ?? [],
    [ccgData],
  );

  const getClaims = useCallback(() => {
    if (!user) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
      return;
    }
    return getPaidClaim({
      filter: {
        status: CLAIM_STATUS.paid,
        ...(searchData && { searchValue: searchData.searchString }),
        ...(ccgFilterData && { claimOrganisationId: ccgFilterData.key }),
        ...(yearFilterData && { searchYear: yearFilterData.key }),
      },
    });
  }, [ccgFilterData, getPaidClaim, handleBannerMessage, searchData, yearFilterData, user]);

  useEffect(() => {
    if (paidClaimData && !paidClaimLoading) {
      setRowData(paidClaimData?.map(mapTableData));
    } else {
      setRowData([]);
    }
    return () => {
      setRowData([]);
    };
  }, [paidClaimData, paidClaimLoading]);

  // Get paid Claim
  useEffect(() => {
    getClaims();
  }, [getClaims]);

  useEffect(() => {
    if (user) {
      getCcg({
        id: user.username,
      });
    }
  }, [getCcg, user]);

  useEffect(() => {
    if (dateRange) {
      getDateRangeData({
        filter: {
          status: CLAIM_STATUS.paid,
          receivedDateFrom: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
          receivedDateTo: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
          ...(searchData && { searchValue: searchData.searchString }),
          ...(ccgFilterData && { claimOrganisationId: ccgFilterData.key }),
          ...(yearFilterData && { searchYear: yearFilterData.key }),
        },
      });
    }
  }, [dateRange, getDateRangeData, searchData, ccgFilterData, yearFilterData]);

  useEffect(() => {
    if (paidClaimError || ccgError || dateRangeDataError) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
    }
  }, [ccgError, dateRangeDataError, handleBannerMessage, paidClaimError]);

  const ccg = useMemo(() => ccgData ?? [], [ccgData]);
  const featuresFromAllOrgs = useMemo(
    () =>
      ccg
        .map((o) => JSON.parse(o?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        .reduce(
          (acc, curr) => ({
            ...acc,
            ...Object.entries(curr)
              .filter(([_, value]) => !!value)
              .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {}),
          }),
          {} as FeatureFlags,
        ),
    [ccg],
  );

  const currentOrg = useMemo(
    () => (ccgFilterData ? ccg.find((o) => o.id === ccgFilterData?.key) : ({} as OrgUserResponse)),
    [ccg, ccgFilterData],
  );
  const featuresFromCurrentOrg = useMemo(
    () =>
      !isEmpty(JSON.parse(currentOrg?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        ? (JSON.parse(currentOrg?.featureFlags ?? INITIAL_FEATURE_FLAG_VALUES) as FeatureFlags)
        : featuresFromAllOrgs,
    [currentOrg?.featureFlags, featuresFromAllOrgs],
  );

  useEffect(() => {
    if (dateRangeData) {
      setCsvData(mapCSVData(dateRangeData));
    }
  }, [dateRangeData]);

  const setSearchDataValue = useCallback(
    (val: FilterData) => {
      setSearchData(val);
    },
    [setSearchData],
  );
  useEffect(() => {
    if (location?.search.length > 0) {
      setClickedRowId(decodeURIComponent(location?.search.substring(1)));
      setShowDetails(true);
      navigate(location.pathname, {
        state: { isSameMenu: false, isInDetailsPage: true },
      });
    }
  }, [location.pathname, location?.search, navigate]);

  return (
    <>
      {showDetails && (
        <PaidDetails
          rowId={clickedRowId}
          goBack={(from: "claimUpdating" | "backButton" | "sideMenuClick") => {
            switch (from) {
              case "sideMenuClick":
              case "backButton":
              case "claimUpdating":
                getClaims();
                break;
              default:
                break;
            }

            setShowDetails(false);
          }}
        />
      )}
      {!showDetails && (
        <Grid
          m={1}
          p={2}
          component={Card}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            borderRadius: 1,
            minHeight: window.innerHeight - 85,
          }}>
          <Grid container m={0}>
            <Grid container spacing={3} sx={{ height: "fit-content" }}>
              <Grid item xs={12}>
                <ProDataGrid
                  title="Paid"
                  subtitle="Claims that have been paid in the last year"
                  rows={rowData}
                  columns={tableColumnList(
                    featuresFromAllOrgs,
                    featuresFromCurrentOrg,
                    ccgFilterData ? ccgFilterData?.key : undefined,
                    "paid",
                  )}
                  ccgList={ccgList}
                  filterData={searchData}
                  filterCcgData={ccgFilterData}
                  filterYearData={yearFilterData}
                  dataError={!paidClaimData && !paidClaimLoading}
                  csvData={csvData}
                  selectedCsvData={selectedCsvData}
                  dateRangeLoading={dateRangeDataLoading}
                  button={{}}
                  dateRange={(val) => {
                    setDateRange(val);
                  }}
                  loading={paidClaimLoading}
                  ccgLoading={ccgLoading}
                  refetchData={() => {
                    setSearchData(null);
                    setYearFilterData(null);
                  }}
                  debouncedSearchString={setSearchDataValue}
                  selectedRows={(rowIds) => {
                    if (paidClaimData) {
                      setSelectedCsvData(mapCSVData(paidClaimData, rowIds));
                    }
                  }}
                  ccgValue={(val) => {
                    setCcgFilterData(val);
                  }}
                  yearValue={(val) => {
                    setYearFilterData(val);
                  }}
                  onClickRowData={(event) => {
                    setClickedRowId(event.id);
                    setShowDetails(true);
                    navigate(location.pathname, {
                      state: { isSameMenu: false, isInDetailsPage: true },
                    });
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default memo(PaidWrapper);
